'use client';

import {
  Button,
  Checkbox,
  CopyButton,
  InlineMessage,
  Text,
  UnorderedList,
} from '@invisionag/components';
import { useTranslation } from '@invisionag/next/i18n';
import { Result } from '@invisionag/next/result';
import { Step4 } from './step-4';
import { downloadBackupCodesPDF } from './recovery-codes-pdf';
import { StepSection } from './step-section';
import { Form, FormMessage, SubmitButton } from '@/lib/form';
import * as Cortex from '@/repositories/cortex';
import { unreachable } from '@/lib/exhaustive';

const keyPrefix = 'login.setup-2fa-form.confirmation';

export function ConfirmationForm({
  recoveryCodes,
  onSuccess,
  onCancel,
  onTimeout,
}: {
  recoveryCodes: string[];
  onSuccess: () => void;
  onCancel: () => void;
  onTimeout: (message: string) => void;
}) {
  const { t } = useTranslation();

  async function enable2fa(formData: FormData) {
    const confirmation = formData.get('confirmation');

    if (confirmation !== 'on') {
      return Result.Err({ message: t(`${keyPrefix}.error.unconfirmed`) });
    }

    const result = await Cortex.enable2fa();

    if (!result.ok) {
      switch (result.err) {
        case 'timeout':
          onTimeout(t(`${keyPrefix}.error.${result.err}`));
          return Result.Err({});
        default:
          unreachable('error', result.err);
      }
    }

    onSuccess();
    return Result.Ok();
  }

  return (
    <Form action={enable2fa} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-[auto_1fr]">
        <StepSection
          icon={<Step4 className="hidden md:block" />}
          title={t(`${keyPrefix}.step-4.heading`)}
        >
          <Text>{t(`${keyPrefix}.step-4.description`)}</Text>
          <InlineMessage variant="warning">
            {t(`${keyPrefix}.step-4.warning`)}
          </InlineMessage>
          <div className="flex max-w-[300px] flex-col gap-4 bg-neutral p-4">
            <div className="flex flex-col gap-4 md:flex-row">
              <UnorderedList className="font-monospace">
                {recoveryCodes.slice(0, 5).map((code) => (
                  <li key={code}>{code}</li>
                ))}
              </UnorderedList>
              <UnorderedList className="font-monospace">
                {recoveryCodes.slice(5).map((code) => (
                  <li key={code}>{code}</li>
                ))}
              </UnorderedList>
            </div>
            <div className="flex justify-center gap-4">
              <Button
                icon="cloud_arrow_down"
                onPress={() => downloadBackupCodesPDF(recoveryCodes)}
              >
                {t(`${keyPrefix}.step-4.download`)}
              </Button>
              <CopyButton
                value={recoveryCodes.join('\n')}
                label={t('common.copy')}
              />
            </div>
          </div>
          <Checkbox isRequired name="confirmation">
            {t(`${keyPrefix}.field.confirmation.label`)}
          </Checkbox>
        </StepSection>
      </div>
      <FormMessage />
      <div className="flex gap-2">
        <SubmitButton>{t(`${keyPrefix}.submit`)}</SubmitButton>
        <Button onPress={onCancel}>{t(`${keyPrefix}.cancel`)}</Button>
      </div>
    </Form>
  );
}
